import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import PushToggle from "@/components/PushToggle";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import OutOfOfficeDelegateCard from "@/components/OutOfOfficeDelegateCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Moon, Sun, BellRing } from "lucide-react";

const WEEKDAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const TRUE_UP_OPTIONS = ["Yes", "No"];
const LANDING_PAGES = [
  { value: "/clock", label: "Dashboard (Clock In)" },
  { value: "/timesheet", label: "My Timesheet" },
  { value: "/tasks", label: "Tasks" },
  { value: "/fleet-status", label: "Fleet Status" },
  { value: "/fleet/maintenance", label: "Maintenance" },
  { value: "/stores/form-generator", label: "Form Generator" },
  { value: "/team/discussion", label: "Discussion" },
  { value: "/announcements", label: "News / Announcements" },
  { value: "/team", label: "Direct Reports", show: (u) => u.can_have_reports },
  { value: "/po-overview", label: "PO Overview", show: (u) => u.po_overview_access || u.role === "admin" },
  { value: "/repair-approvals", label: "Repairs", show: (u) => u.repair_approvals_access || u.role === "admin" },
  { value: "/capital", label: "Capital Overview", show: (u) => u.capital_access || u.role === "admin" },
  { value: "/gse", label: "GSE", show: (u) => u.gse_access || u.role === "admin" },
  { value: "/stores/critical-spares", label: "Critical Spares", show: (u) => u.critical_spares_access || u.role === "admin" },
  { value: "/stores/spares-monitoring", label: "Spares Monitoring", show: (u) => u.spares_monitoring_access || u.role === "admin" },
  { value: "/stores/parts", label: "Parts", show: (u) => u.parts_access || u.role === "admin" },
  { value: "/fleet/planning", label: "Fleet Planning", show: (u) => u.planning_access || u.role === "admin" },
  { value: "/fleet/heavy-checks", label: "Heavy Checks", show: (u) => u.heavy_check_access || u.role === "admin" },
];

export default function Profile() {
  const { user, setUser, refresh } = useAuth();
  const [locations, setLocations] = useState([]);
  const [shiftSchedules, setShiftSchedules] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/shift-schedules").then((r) => setShiftSchedules(r.data));
    api.get("/cities").then((r) => setCities(r.data)).catch(() => setCities([]));
  }, []);
  useEffect(() => {
    if (user) setForm({ ...user });
  }, [user]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleTheme = async (v) => {
    const theme = v ? "dark" : "light";
    set("theme_pref", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
    try { localStorage.setItem("theme_pref", theme); } catch { /* ignore */ }
    try {
      await api.patch("/me/theme", { theme });
      toast.success(theme === "dark" ? "Dark scheme saved" : "Light scheme saved");
    } catch {
      toast.error("Failed to save theme preference");
    }
  };

  const toggleDutyAlert = async (v) => {
    set("duty_alert_enabled", v);
    try {
      const { data } = await api.patch("/profile", { duty_alert_enabled: v });
      setUser(data);
      toast.success(v ? "Start-duty alert on — you'll be asked at every login." : "Start-duty alert off");
    } catch {
      set("duty_alert_enabled", !v);
      toast.error("Failed to save duty alert preference");
    }
  };

  const setLandingPage = async (v) => {
    set("landing_page", v);
    try {
      const { data } = await api.patch("/profile", { landing_page: v });
      setUser(data);
      const opt = LANDING_PAGES.find((p) => p.value === v);
      toast.success(`Landing page set to ${opt?.label || v}`);
    } catch {
      toast.error("Failed to save landing page");
    }
  };

  const validate = () => {
    if (!form.first_name?.trim()) return "First name is required.";
    if (!form.last_name?.trim()) return "Last name is required.";
    if (!form.first_initial?.trim()) return "First name initial is required.";
    if (!form.employee_number?.trim()) return "Employee number is required.";
    if (!form.location) return "Location of Work is mandatory.";
    if (!form.city) return "City is mandatory.";
    if (!form.true_up_applied) return "True-up to be applied is mandatory.";
    if (!form.max_daily_hours || form.max_daily_hours <= 0) return "Max regular daily hours is required.";
    if (!form.shift_schedule) return "Shift schedule is required.";
    if (!form.first_day_of_shift) return "First day of shift is required.";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", {
        first_name: form.first_name,
        last_name: form.last_name,
        first_initial: form.first_initial,
        employee_number: form.employee_number,
        location: form.location,
        city: form.city,
        per_diem_cdn: !!form.per_diem_cdn,
        per_diem_int: !!form.per_diem_int,
        true_up_applied: form.true_up_applied,
        max_daily_hours: parseFloat(form.max_daily_hours),
        shift_schedule: form.shift_schedule,
        first_day_of_shift: form.first_day_of_shift,
      });
      setUser(data);
      toast.success("Profile updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="profile-page">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Profile · MST</div>
      <h1 className="font-display text-4xl font-semibold tracking-tight mt-1">Your details</h1>
      <p className="text-sm text-gray-500 mt-1">Used when you clock in. Display format: Lastname, F.</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">First name</Label>
          <Input className="mt-1.5" value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="profile-first-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Last name</Label>
          <Input className="mt-1.5" value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="profile-last-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">First name initial</Label>
          <Input className="mt-1.5" maxLength={1} value={form.first_initial || ""} onChange={(e) => set("first_initial", e.target.value.toUpperCase())} data-testid="profile-first-initial" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Employee number</Label>
          <Input className="mt-1.5 font-mono" value={form.employee_number || ""} onChange={(e) => set("employee_number", e.target.value)} data-testid="profile-employee-number" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-gray-500">Location of work · required</Label>
          <Select value={form.location || ""} onValueChange={(v) => set("location", v)}>
            <SelectTrigger className="mt-1.5" data-testid="profile-location-trigger">
              <SelectValue placeholder="Select your location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.code} value={loc.code} data-testid={`location-option-${loc.code}`}>
                  {loc.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-gray-500">City (base) · required</Label>
          <Select value={form.city || ""} onValueChange={(v) => set("city", v)}>
            <SelectTrigger className="mt-1.5" data-testid="profile-city-trigger">
              <SelectValue placeholder="Select your base city" />
            </SelectTrigger>
            <SelectContent>
              {cities.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`city-option-${c.code}`}>
                  {c.code} · {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">True-up to be applied · required</Label>
          <Select value={form.true_up_applied || ""} onValueChange={(v) => set("true_up_applied", v)}>
            <SelectTrigger className="mt-1.5" data-testid="profile-true-up-trigger">
              <SelectValue placeholder="Yes / No" />
            </SelectTrigger>
            <SelectContent>
              {TRUE_UP_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`true-up-option-${o.toLowerCase()}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Max regular daily hours · required</Label>
          <Input
            className="mt-1.5 font-mono"
            type="number"
            step="0.1"
            min="0"
            max="24"
            value={form.max_daily_hours ?? ""}
            onChange={(e) => set("max_daily_hours", e.target.value)}
            data-testid="profile-max-daily-hours"
            placeholder="e.g. 11.4 or 8"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Shift schedule · required</Label>
          <Select value={form.shift_schedule || ""} onValueChange={(v) => set("shift_schedule", v)}>
            <SelectTrigger className="mt-1.5" data-testid="profile-shift-schedule-trigger">
              <SelectValue placeholder="Select shift" />
            </SelectTrigger>
            <SelectContent>
              {shiftSchedules.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`shift-option-${s.id}`}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">First day of shift · required</Label>
          <Select value={form.first_day_of_shift || ""} onValueChange={(v) => set("first_day_of_shift", v)}>
            <SelectTrigger className="mt-1.5" data-testid="profile-first-day-trigger">
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAYS.map((d) => (
                <SelectItem key={d} value={d} data-testid={`weekday-option-${d.toLowerCase()}`}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.shift_schedule === "7-on-off" && form.first_day_of_shift && (
            <p className="mt-1 text-[11px] text-gray-500">
              7 on / 7 off — schedule treated as {form.first_day_of_shift} through {WEEKDAYS[(WEEKDAYS.indexOf(form.first_day_of_shift) + 6) % 7]}.
            </p>
          )}
        </div>
      </div>

      <div className="mt-10 border-t border-summit-hairline pt-8">
        <h2 className="font-display text-xl font-medium tracking-tight">Shift range · auto-projection</h2>
        <p className="text-sm text-gray-500 mt-1">
          Mark one full shift on the calendar (e.g. Mon 29 Jun → Sun 5 Jul = 7 days on).
          The system infers your cycle (symmetric on/off) and projects future days off across the app.
        </p>
        <ShiftRangePicker userId={user.id} initialStart={form.shift_range_start} initialEnd={form.shift_range_end} initialOverride={form.shift_days_off_override} />
      </div>

      <div className="mt-10 border-t border-summit-hairline pt-8">
        <h2 className="font-display text-xl font-medium tracking-tight">Per diems · optional</h2>
        <p className="text-sm text-gray-500 mt-1">Only enable if applicable to your clock-in hours.</p>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div className="border border-summit-hairline rounded-md p-4 flex items-center justify-between">
            <div>
              <div className="font-medium">Canadian per diem</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">$80 / day</div>
            </div>
            <Switch
              checked={!!form.per_diem_cdn}
              onCheckedChange={(v) => set("per_diem_cdn", v)}
              data-testid="profile-per-diem-cdn"
            />
          </div>
          <div className="border border-summit-hairline rounded-md p-4 flex items-center justify-between">
            <div>
              <div className="font-medium">International per diem</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">$100 / day</div>
            </div>
            <Switch
              checked={!!form.per_diem_int}
              onCheckedChange={(v) => set("per_diem_int", v)}
              data-testid="profile-per-diem-int"
            />
          </div>
        </div>
      </div>

      <div className="mt-10 border-t border-summit-hairline pt-8">
        <h2 className="font-display text-xl font-medium tracking-tight">Appearance</h2>
        <p className="text-sm text-gray-500 mt-1">Choose your preferred color scheme — saved to your account.</p>
        <div className="mt-5 border border-summit-hairline rounded-md p-4 flex items-center justify-between md:max-w-md">
          <div>
            <div className="font-medium flex items-center gap-2">
              {form.theme_pref === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Dark mode
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              {form.theme_pref === "dark" ? "Dark scheme active" : "Light scheme active"}
            </div>
          </div>
          <Switch
            checked={form.theme_pref === "dark"}
            onCheckedChange={toggleTheme}
            data-testid="profile-theme-toggle"
          />
        </div>
      </div>

      <div className="mt-10 border-t border-summit-hairline pt-8">
        <h2 className="font-display text-xl font-medium tracking-tight">Duty reminder</h2>
        <p className="text-sm text-gray-500 mt-1">Get a "Start duty?" prompt every time you log in — one tap stamps your shift log.</p>
        <div className="mt-5 border border-summit-hairline rounded-md p-4 flex items-center justify-between md:max-w-md">
          <div>
            <div className="font-medium flex items-center gap-2">
              <BellRing className="w-4 h-4" /> Start duty alert at login
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              {form.duty_alert_enabled ? "Alert on" : "Alert off"}
            </div>
          </div>
          <Switch
            checked={!!form.duty_alert_enabled}
            onCheckedChange={toggleDutyAlert}
            data-testid="profile-duty-alert-toggle"
          />
        </div>
      </div>

      <div className="mt-10 border-t border-summit-hairline pt-8">
        <h2 className="font-display text-xl font-medium tracking-tight">Landing page</h2>
        <p className="text-sm text-gray-500 mt-1">Choose the page you land on when you log in. Saves instantly.</p>
        <div className="mt-5 md:max-w-md">
          <Select value={form.landing_page || "/clock"} onValueChange={setLandingPage}>
            <SelectTrigger data-testid="profile-landing-page-trigger">
              <SelectValue placeholder="Dashboard (Clock In)" />
            </SelectTrigger>
            <SelectContent>
              {LANDING_PAGES.filter((p) => !p.show || p.show(user)).map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`landing-option-${p.value.replace(/\//g, "-").slice(1)}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-10">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Notifications</div>
        <PushToggle />
      </div>

      <div className="mt-10 flex justify-end">
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={saving} data-testid="profile-save">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </div>
    </div>
  );
}

function ShiftRangePicker({ initialStart, initialEnd, initialOverride }) {
  const [start, setStart] = useState(initialStart || "");
  const [end, setEnd] = useState(initialEnd || "");
  const [override, setOverride] = useState(initialOverride ?? "");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const { refresh } = useAuth();

  useEffect(() => {
    setStart(initialStart || "");
    setEnd(initialEnd || "");
    setOverride(initialOverride ?? "");
  }, [initialStart, initialEnd, initialOverride]);

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/shift/projected-days-off?months=3");
      setPreview(data);
    } catch (e) {
      // Preview is decorative — a failure here shouldn't block the form.
      console.warn("Failed to load shift-range preview:", e);
    }
  };
  useEffect(() => { if (initialStart && initialEnd) loadPreview(); }, [initialStart, initialEnd]);

  const save = async () => {
    // Both must be set OR both blank (matches backend rule).
    if ((!start && end) || (start && !end)) {
      toast.error("Shift start and end must be set together.");
      return;
    }
    if (!start || !end) { toast.error("Pick both dates"); return; }
    if (end < start) { toast.error("End date must be on or after start date"); return; }
    setSaving(true);
    try {
      const payload = {
        shift_range_start: start,
        shift_range_end: end,
        shift_days_off_override: override === "" ? null : Number(override),
      };
      await api.patch("/users/me/shift-range", payload);
      toast.success("Shift range saved");
      await refresh();
      await loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/shift-range", { shift_range_start: "", shift_range_end: "", shift_days_off_override: null });
      toast.success("Cleared");
      setStart(""); setEnd(""); setOverride(""); setPreview(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  // Group consecutive projected dates into ranges for a compact preview.
  const groups = React.useMemo(() => {
    if (!preview?.projected?.length) return [];
    const out = [];
    let s = preview.projected[0];
    let e = s;
    for (let i = 1; i < preview.projected.length; i++) {
      const d = preview.projected[i];
      const prev = new Date(e); prev.setDate(prev.getDate() + 1);
      if (d === prev.toISOString().slice(0, 10)) { e = d; }
      else { out.push([s, e]); s = d; e = d; }
    }
    out.push([s, e]);
    return out;
  }, [preview]);

  return (
    <div className="mt-5">
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Shift start</Label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="w-full h-10 mt-1.5 px-3 rounded-md border border-summit-hairline text-sm"
            data-testid="shift-range-start"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Shift end</Label>
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            className="w-full h-10 mt-1.5 px-3 rounded-md border border-summit-hairline text-sm"
            data-testid="shift-range-end"
          />
        </div>
      </div>
      <div className="mt-3 max-w-md">
        <Label className="text-xs uppercase tracking-widest text-gray-500">
          Days off (optional · leave blank for symmetric)
        </Label>
        <input
          type="number"
          min="0"
          max="60"
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          placeholder="e.g. 2 for 5-on-2-off, 7 for 7-on-7-off (default)"
          className="w-full h-10 mt-1.5 px-3 rounded-md border border-summit-hairline text-sm"
          data-testid="shift-days-off"
        />
        <p className="mt-1.5 font-mono text-[10px] text-gray-400">
          Blank → symmetric (days off = days on). Set to override for 5-2, 4-3, 14-14 etc.
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={saving} data-testid="shift-range-save">
          {saving ? "Saving…" : "Save shift range"}
        </Button>
        {(start || end) && (
          <Button variant="outline" onClick={clear} disabled={saving} data-testid="shift-range-clear">
            Clear
          </Button>
        )}
      </div>
      {preview && preview.days_on ? (
        <div className="mt-5 border border-summit-hairline rounded-md bg-summit-glacier-soft p-4" data-testid="shift-preview">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
            Cycle: {preview.days_on} days on · {preview.days_off} days off
          </div>
          <div className="mt-2 text-sm text-gray-700">
            <span className="font-medium">Next projected days off:</span>
            <ul className="mt-1.5 space-y-0.5 font-mono text-xs">
              {groups.slice(0, 6).map(([s, e], idx) => (
                <li key={`${s}-${e}`} data-testid={`projected-range-${idx}`}>
                  {s}{s === e ? "" : ` → ${e}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      <ChangePasswordCard />
      <OutOfOfficeDelegateCard />
    </div>
  );
}
